import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SearchFilters = ({
  categories,
  authors,
  selectedCategory,
  selectedAuthor,
  sortBy,
  onCategoryChange,
  onAuthorChange,
  onSortChange,
  resultsCount
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const sortOptions = [
    { value: 'latest', label: 'Latest First', icon: 'Calendar' },
    { value: 'oldest', label: 'Oldest First', icon: 'CalendarDays' },
    { value: 'popular', label: 'Most Popular', icon: 'TrendingUp' },
    { value: 'comments', label: 'Most Discussed', icon: 'MessageCircle' }
  ];

  const toggleFilters = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };

  const clearAllFilters = () => {
    onCategoryChange('all');
    onAuthorChange('all');
    onSortChange('latest');
  };

  const hasActiveFilters = selectedCategory !== 'all' || selectedAuthor !== 'all' || sortBy !== 'latest';

  return (
    <div className="mb-8">
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-4">
        <button
          onClick={toggleFilters}
          className="flex items-center justify-between w-full p-4 bg-surface rounded-lg border border-border"
        >
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-text-secondary" />
            <span className="font-medium text-text-primary">Filters & Sort</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-primary rounded-full"></span>
            )}
          </div>
          <Icon 
            name={isFiltersOpen ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-text-secondary" 
          />
        </button>
      </div>

      {/* Filter Controls */}
      <div className={`${isFiltersOpen ? 'block' : 'hidden'} lg:block`}>
        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Results Count */}
            <div className="flex items-center space-x-2">
              <Icon name="FileText" size={20} className="text-text-secondary" />
              <span className="text-sm text-text-secondary">
                {resultsCount} {resultsCount === 1 ? 'article' : 'articles'} found
              </span>
            </div>

            {/* Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Category Filter */}
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => onCategoryChange(e.target.value)}
                  className="appearance-none bg-background border border-border rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-40"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.count})
                    </option>
                  ))}
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-secondary pointer-events-none" 
                />
              </div>

              {/* Author Filter */}
              <div className="relative">
                <select
                  value={selectedAuthor}
                  onChange={(e) => onAuthorChange(e.target.value)}
                  className="appearance-none bg-background border border-border rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-40"
                >
                  {authors.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-secondary pointer-events-none" 
                />
              </div>

              {/* Sort Filter */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => onSortChange(e.target.value)}
                  className="appearance-none bg-background border border-border rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-w-40"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-secondary pointer-events-none" 
                />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary border border-border rounded-md hover:bg-secondary-100 transition-colors duration-150"
                >
                  <Icon name="X" size={16} />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-text-secondary">Active filters:</span>
                {selectedCategory !== 'all' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700">
                    Category: {categories.find(c => c.id === selectedCategory)?.name}
                    <button
                      onClick={() => onCategoryChange('all')}
                      className="ml-1 hover:text-primary-900"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
                {selectedAuthor !== 'all' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-700">
                    Author: {authors.find(a => a.id === selectedAuthor)?.name}
                    <button
                      onClick={() => onAuthorChange('all')}
                      className="ml-1 hover:text-accent-900"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
                {sortBy !== 'latest' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700">
                    Sort: {sortOptions.find(s => s.value === sortBy)?.label}
                    <button
                      onClick={() => onSortChange('latest')}
                      className="ml-1 hover:text-secondary-900"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;